/**
 * useDraftingActivity — the audit trail of one drafted document.
 *
 * The page used to hand the whole `AuditLogPanel` into the activity pane,
 * which meant the fetch, the paging AND the refresh button all lived below the
 * pane header — so the pane drew a header with a close button and the body
 * drew a second toolbar row underneath it just to hold "Refresh".
 *
 * Splitting the state out is what lets the REFRESH live in the pane header,
 * where every other pane control on this surface lives, while the body stays
 * the shared `activity/ActivityPane` list. Nothing about the rows is
 * drafting-specific — only the endpoint is, and that is all this owns.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import apis from '../../../utils/apis';
import {
  ActivityEntry,
  normalizeAuditPayload,
  toActivityEntries,
} from '../../shared/activity/activityModel';
import { useOrgMembers } from '../../../hooks/useOrgMembers';
import type { DraftingKind } from '../../../store/slices/draftingSlice';

export interface DraftingActivityState {
  entries: ActivityEntry[];
  loading: boolean;
  hasMore: boolean;
  /** Reload from page 1 — the pane header's refresh control. */
  refresh: () => Promise<void>;
  /** Append the next page. */
  loadMore: () => Promise<void>;
}

/**
 * The audit endpoint knows WHO but not WHICH ACCOUNT.
 *
 * `_read_entity_audit_log` decrypts each row's user uuid, looks the user up
 * and returns `user_first_name` / `user_last_name` — and stops there. No
 * email, no profile picture. `ActivityEntry.actorEmail` / `.actorAvatar` are
 * documented as fields a host with a people directory fills in, and this pane
 * never filled them, so `showActorAvatar` could only ever draw initials: a
 * column of grey discs on a surface whose whole point is that a handful of
 * named colleagues worked this document.
 *
 * The org directory is already a hook (`useOrgMembers` — the same rows the
 * collaborator pickers use), so the join happens here, by display name. Name
 * collisions inside one firm are possible and the cost of one is an avatar,
 * not a wrong attribution: the NAME still comes from the audit row either way.
 */
const normalizeName = (name: string): string =>
  name.trim().toLowerCase().replace(/\s+/g, ' ');

/** Drafts and strategies are two collections behind one detail route. */
const auditPath = (kind: DraftingKind, uuid: string): string =>
  kind === 'strategy'
    ? `/drafting/insights/${uuid}/audit-log`
    : `/drafting/drafts/${uuid}/audit-log`;

export function useDraftingActivity(
  kind: DraftingKind | undefined,
  uuid: string | undefined,
): DraftingActivityState {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const { members } = useOrgMembers();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async (pageNum: number) => {
    if (!kind || !uuid) return;
    setLoading(true);
    try {
      const res = await apis.get(auditPath(kind, uuid), {
        params: { page: pageNum, limit: 50 },
      });
      const { entries: list, hasNext } = normalizeAuditPayload(res?.data);
      const mapped = toActivityEntries(list);
      setEntries((prev) => (pageNum === 1 ? mapped : [...prev, ...mapped]));
      setHasMore(hasNext);
      setPage(pageNum);
    } catch {
      // A failed refresh must not blank a list that is already on screen.
      if (pageNum === 1) setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [kind, uuid]);

  useEffect(() => { load(1); }, [load]);

  const refresh = useCallback(() => load(1), [load]);
  const loadMore = useCallback(() => load(page + 1), [load, page]);

  const directory = useMemo(() => {
    const byName = new Map<string, { email: string; avatarUrl: string | null }>();
    for (const member of members) {
      const key = normalizeName(member.name || '');
      // First writer wins, so a duplicate name cannot flip an already-resolved
      // face on a later refetch.
      if (key && !byName.has(key)) {
        byName.set(key, { email: member.email, avatarUrl: member.avatarUrl });
      }
    }
    return byName;
  }, [members]);

  /* The rows as fetched, plus a face for every actor the directory knows. */
  const resolved = useMemo(
    () => entries.map((entry) => {
      const match = entry.actor ? directory.get(normalizeName(entry.actor)) : undefined;
      if (!match) return entry;
      return {
        ...entry,
        actorEmail: entry.actorEmail ?? match.email,
        actorAvatar: entry.actorAvatar ?? match.avatarUrl,
      };
    }),
    [entries, directory],
  );

  return { entries: resolved, loading, hasMore, refresh, loadMore };
}

export default useDraftingActivity;
